import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';

// --- RILEVAMENTO TEMA DI SISTEMA ---
const isSystemDark = typeof window !== 'undefined' && 
  window.matchMedia('(prefers-color-scheme: dark)').matches;

// --- CONFIGURAZIONE TIPOGRAFICA MD3 ---
const baseTypography = {
  fontFamily: '"Plus Jakarta Sans", "Roboto", "Inter", sans-serif', // Font moderni e geometrici
  h1: { fontWeight: 700, letterSpacing: '-0.025em' },
  h2: { fontWeight: 600, letterSpacing: '-0.02em' },
  h3: { fontWeight: 600, letterSpacing: '0em' },
  h4: { fontWeight: 600 },
  h5: { fontWeight: 600 },
  h6: { fontWeight: 600, letterSpacing: '0.01em' },
  button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.02em' }, // MD3 no caps
  body1: { letterSpacing: '0.01em', lineHeight: 1.6 },
  body2: { letterSpacing: '0.01em', lineHeight: 1.5 },
};

// --- HELPER PER L'EFFETTO GLASS ---
const createGlassStyle = (color, opacity = 0.7, blur = 20) => ({
  backgroundColor: alpha(color, opacity),
  backdropFilter: `blur(${blur}px) saturate(180%)`, // Saturazione aumenta la vividezza dietro il vetro
  border: `1px solid ${alpha('#FFFFFF', 0.3)}`,
  boxShadow: `0 8px 32px 0 ${alpha(color, 0.1)}`,
});

// =============================================================================
// 1. TEMA MONITOR (Core Business / Deep Blue)
// Colore: #24476A (Deep Corporate Blue)
// Vibe: Solido, Affidabile, Vetro Scuro/Elegante
// =============================================================================

const monitorPalette = isSystemDark ? {
  // DARK MODE MONITOR
  mode: 'dark',
  primary: { main: '#82B1FF', contrastText: '#002040' }, // Azzurro acceso per contrasto su scuro
  secondary: { main: '#24476A', contrastText: '#FFFFFF' },
  background: { 
    default: '#0B1521', // Quasi nero, tinta blu profonda
    paper: '#132235',   
  },
  text: { primary: '#ECF0F3', secondary: '#9BA7B6' },
  divider: alpha('#24476A', 0.2),
} : {
  // LIGHT MODE MONITOR
  mode: 'light',
  primary: { main: '#24476A', contrastText: '#FFFFFF' }, // Il tuo colore aziendale principale
  secondary: { main: '#547294', contrastText: '#FFFFFF' },
  background: { 
    default: '#F4F7FA', // Grigio-blu chiarissimo
    paper: '#FFFFFF', 
  },
  text: { primary: '#1A2C42', secondary: '#586A84' },
  divider: alpha('#24476A', 0.1),
};

let monitorTheme = createTheme({
  palette: monitorPalette,
  typography: baseTypography,
  shape: { borderRadius: 16 }, // MD3 Standard corner
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { 
          // Sfondo con gradiente sottile per far risaltare il glass
          backgroundImage: isSystemDark 
            ? 'radial-gradient(circle at 10% 20%, #15283F 0%, #0B1521 90%)'
            : 'radial-gradient(circle at 10% 20%, #E8F0F6 0%, #F4F7FA 90%)',
          backgroundAttachment: 'fixed',
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          ...createGlassStyle(monitorPalette.background.paper, isSystemDark ? 0.6 : 0.8),
          borderRadius: 16,
        },
        elevation1: { boxShadow: '0 4px 20px rgba(36, 71, 106, 0.08)' }, // Ombra bluastra
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 24, // Pill shape (MD3)
          padding: '10px 24px',
        },
        containedPrimary: {
          boxShadow: `0 8px 16px ${alpha('#24476A', 0.25)}`,
          '&:hover': {
            boxShadow: `0 12px 20px ${alpha('#24476A', 0.35)}`,
            transform: 'translateY(-1px)', // Micro-interazione
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(monitorPalette.background.paper, 0.6),
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${alpha('#24476A', 0.08)}`,
          boxShadow: 'none',
          color: monitorPalette.text.primary,
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(4px)',
          border: `1px solid ${alpha('#24476A', 0.1)}`,
        },
        colorPrimary: {
          backgroundColor: alpha('#24476A', 0.1),
          color: isSystemDark ? '#82B1FF' : '#24476A',
          fontWeight: 600,
        }
      }
    }
  },
});

// =============================================================================
// 2. TEMA EXTRA CONTENT (Detail / Lighter Blue)
// Colore: #336699 (Steel Blue)
// Vibe: Fluido, Cristallino, "Aria", Contenuto Premium
// Distinzione: Forme più ampie, ombre più morbide, palette più luminosa.
// =============================================================================

const extraPalette = isSystemDark ? {
  // DARK MODE EXTRA
  mode: 'dark',
  primary: { main: '#64B5F6', contrastText: '#0D2746' }, // Azzurro cielo
  secondary: { main: '#336699', contrastText: '#FFFFFF' },
  background: { 
    default: '#0F1E30', // Blu notte leggermente più chiaro del monitor
    paper: '#182C45',   
  },
  text: { primary: '#E3F2FD', secondary: '#90CAF9' },
  divider: alpha('#336699', 0.2),
} : {
  // LIGHT MODE EXTRA
  mode: 'light',
  primary: { main: '#336699', contrastText: '#FFFFFF' }, // Il tuo secondo colore aziendale
  secondary: { main: '#6390BD', contrastText: '#FFFFFF' },
  background: { 
    default: '#F0F8FF', // AliceBlue (sottile distinzione dal monitor)
    paper: '#FFFFFF', 
  },
  text: { primary: '#153E6B', secondary: '#486D94' },
  divider: alpha('#336699', 0.15),
};

let extraContentTheme = createTheme({
  palette: extraPalette,
  typography: baseTypography,
  shape: { borderRadius: 16 }, // Curve più accentuate per differenziare "Extra"
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { 
          // Gradiente diverso per distinguere visivamente la modalità
          backgroundImage: isSystemDark 
            ? 'linear-gradient(135deg, #0F1E30 0%, #1A3556 100%)'
            : 'linear-gradient(135deg, #F0F8FF 0%, #E3EFFB 100%)',
          backgroundAttachment: 'fixed',
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          // Glass più "Strong" per i contenuti extra
          ...createGlassStyle(extraPalette.background.paper, isSystemDark ? 0.5 : 0.75, 30),
          border: `1px solid ${alpha('#336699', 0.15)}`, // Bordo leggermente colorato
          borderRadius: 16,
        },
        elevation1: { boxShadow: `0 10px 40px -10px ${alpha('#336699', 0.2)}` }, // Ombra diffusa colorata (Glow)
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 24, // Pillola completa
          fontSize: '1rem',
          padding: '12px 32px',
        },
        containedPrimary: {
          // Gradiente sottile tono su tono per renderlo premium
          background: `linear-gradient(135deg, #336699 0%, #407AB0 100%)`,
          boxShadow: `0 8px 20px ${alpha('#336699', 0.3)}`,
          '&:hover': {
            background: `linear-gradient(135deg, #2A5580 0%, #336699 100%)`,
            boxShadow: `0 12px 25px ${alpha('#336699', 0.4)}`,
          },
        },
        outlinedPrimary: {
          borderWidth: 2,
          borderColor: alpha('#336699', 0.5),
          '&:hover': { borderWidth: 2, borderColor: '#336699', backgroundColor: alpha('#336699', 0.05) }
        }
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8, // Più squadrato nel tema Extra per contrasto con le card tonde
          fontWeight: 700,
        },
        colorPrimary: {
          backgroundColor: isSystemDark ? alpha('#64B5F6', 0.15) : alpha('#336699', 0.1),
          color: isSystemDark ? '#64B5F6' : '#336699',
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16, // Dialoghi molto tondi
          backdropFilter: 'blur(20px)',
        }
      }
    }
  },
});

monitorTheme = responsiveFontSizes(monitorTheme);
extraContentTheme = responsiveFontSizes(extraContentTheme);

export { monitorTheme, extraContentTheme };